/**
 * @fileoverview General helper utilities
 * @module utils/helpers
 */

import Swal from 'sweetalert2';
import { translateMessage, translateTitle } from './backendMessage';

/**
 * Generate random alphanumeric string
 * @param {number} length - Length of string to generate
 * @returns {string} Random string
 *
 * @example
 * random_charactor(8) // "a1B2c3D4"
 */
export const random_charactor = (length) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length))
  ).join('');
};

/**
 * Copy text to clipboard
 * @param {string} textToCopy - Text to copy
 * @returns {Promise} Resolves when copied
 */
export const copyText = (textToCopy) => {
  return navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      Swal.fire({
        icon: 'success',
        title: translateTitle('Success'),
        text: translateMessage('Copied successfully'),
        showConfirmButton: false,
        timer: 1500,
        background: '#fff',
        color: '#000000',
        confirmButtonColor: '#212529',
        cancelButtonColor: 'unset',
      });
    })
    .catch((err) => {
      console.error('Failed to copy:', err);
    });
};

/**
 * Get URL query parameter
 * @param {string} name - Parameter name
 * @returns {string|null} Parameter value
 *
 * @example
 * // URL: /page?id=123
 * params('id') // "123"
 */
export const params = (name) => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get(name);
};

/**
 * Required field indicator JSX
 */
export const required = (<span class="text-danger">*</span>);

/**
 * Debounce function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function execution
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Sleep/delay function
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Resolves after delay
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param {*} value - Value to check
 * @returns {boolean} True if empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * ตรวจว่ารายการ master นี้ยัง "เปิดใช้งาน" อยู่หรือไม่
 *
 * - `is_active === false` -> ปิดใช้งาน (ใช้กับทุกตาราง master)
 * - `customer_status === "inactive"` -> ปิดใช้งาน (ลูกค้ามีสถานะแยกอีกชั้น)
 *   หมายเหตุ: "HOLD" ยังถือว่าเปิดใช้งานอยู่ เพราะ backend ตั้งใจให้เปิดใบแจ้งซ่อมได้
 *
 * @param {object} item - รายการจาก API
 * @returns {boolean} true ถ้ายังเลือกใช้งานได้
 */
export const isOptionActive = (item) => {
  if (!item || typeof item !== 'object') return true;

  const customerStatus =
    typeof item.customer_status === 'string' ? item.customer_status.trim().toLowerCase() : '';

  // ลูกค้าที่ตั้งสถานะเป็น "inactive" ถือว่าปิดใช้งาน ไม่ว่า is_active จะเป็นอะไร
  if (customerStatus === 'inactive') return false;
  // "hold" ยังเปิดใบแจ้งซ่อมได้ จึงยังต้องเลือกได้
  if (customerStatus === 'hold') return true;

  if (item.is_active === false) return false;
  return true;
};

/** คำว่า "ปิดใช้งาน" ตามภาษาปัจจุบัน (helpers.js เรียก t() ไม่ได้ เพราะจะ import วน) */
const inactiveWord = () =>
  (localStorage.getItem('lang') || 'TH') === 'EN' ? 'Inactive' : 'ปิดใช้งาน';

const toKeyList = (value) =>
  (Array.isArray(value) ? value : [value])
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map(String);

/**
 * กรองรายการที่ปิดใช้งานออกจาก dropdown/select
 *
 * เก็บ list เต็มไว้ใน state เหมือนเดิม (ยังใช้เป็น lookup ของข้อมูลเก่าได้) แล้วห่อด้วยฟังก์ชันนี้
 * เฉพาะตอนส่งเข้า `options` ค่าที่ถูกเลือกอยู่ (`current`) จะถูกคงไว้เสมอแม้จะปิดใช้งานไปแล้ว
 * ไม่งั้น Select จะหา label ไม่เจอ -> ช่องว่างเปล่า -> ผู้ใช้กดบันทึกแล้วค่าเดิมหายโดยไม่ตั้งใจ
 *
 * @param {Array} list - รายการทั้งหมดจาก state
 * @param {string|number|Array} [current] - ค่าที่เลือกอยู่ (รองรับ multi-select)
 * @param {object} [config]
 * @param {string[]} [config.keys=['value','uid','id']] - field ที่ใช้จับคู่กับ current
 * @param {boolean} [config.mark=true] - ต่อท้าย label ว่า "(ปิดใช้งาน)" ให้ตัวที่คงไว้
 * @param {Function} [config.isActive=isOptionActive] - เงื่อนไข active แบบกำหนดเอง
 * @returns {Array} รายการที่กรองแล้ว
 *
 * @example
 * <Select options={activeOptions(data_customer, create_customer_id)} ... />
 */
export const activeOptions = (list, current, config = {}) => {
  if (!Array.isArray(list)) return [];
  const {
    keys = ['value', 'uid', 'id'],
    mark = true,
    isActive = isOptionActive,
  } = config;

  const selected = toKeyList(current);
  const isSelected = (item) =>
    selected.length > 0 &&
    keys.some((k) => {
      const v = item?.[k];
      return v !== undefined && v !== null && selected.includes(String(v));
    });

  const result = [];
  for (const item of list) {
    if (isActive(item)) {
      result.push(item);
    } else if (isSelected(item)) {
      result.push(
        mark && item?.label
          ? { ...item, label: `${item.label} (${inactiveWord()})`, is_inactive_kept: true }
          : { ...item, is_inactive_kept: true }
      );
    }
  }
  return result;
};

/**
 * ช่างที่ใช้งานอยู่ = is_active ไม่เป็น false และสถานะ (ถ้ามี) เป็น active
 * ค่าเก่า resigned/suspended ถือเป็นไม่ใช้งาน (ใช้กับ dropdown มอบหมาย/เลือกช่างใหม่เท่านั้น)
 */
export const isTechnicianActive = (item) => {
  if (!item || typeof item !== 'object') return true;
  if (item.is_active === false) return false;
  const status = typeof item.status === 'string' ? item.status.trim().toLowerCase() : '';
  return !status || status === 'active';
};

const helpers = {
  random_charactor,
  copyText,
  params,
  required,
  debounce,
  throttle,
  sleep,
  isEmpty,
  isOptionActive,
  activeOptions,
  isTechnicianActive,
};

export default helpers;
